import { SearchResult } from "#next/utils/interfaces";
import {
  Menu,
  MenuItem,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import { trackAction } from "#next/utils/trackUserActions";
import { trpc } from "#next/utils/trpc";
import { zSharedPost } from "#next/utils/shared";
import React, { useState, useContext } from "react";
import { useUser } from "@auth0/nextjs-auth0/client";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { PostContext } from "#next/components/post/contexts";
import { ConfirmDialog } from "#next/components/reusable/ConfirmDialog";

export const Buttons = ({
  post,
  hideSinglePostButton,
}: {
  post: SearchResult;
  hideSinglePostButton?: boolean;
}) => {
  const { trackActionPrefix } = useContext(PostContext);

  if (trackActionPrefix === null) {
    throw new Error("trackActionPrefix cannot be null");
  }

  const { user } = useUser();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      justifyContent="flex-end"
      alignItems="center"
      sx={{ display: "flex", wrap: "nowrap", flexGrow: 1 }}
      // Otherwise clicks get handled by the parent card as well, which opens the SinglePost modal
      onClick={(e) => e.stopPropagation()}
    >
      <IconButton id="options-menu-button" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {!hideSinglePostButton && <SinglePostButton row={post} />}
        {user && <SharePostButton post={post} />}
        {post.url && (
          <NavigateToSourceButton
            url={post.url}
            trackActionFn={() =>
              trackAction(`${trackActionPrefix} > Feed > Open Source`)
            }
          />
        )}
        {post.url && (
          <CopyLinkToClipboardButton
            url={post.url}
            trackActionFn={() =>
              trackAction(`${trackActionPrefix} > Feed > Copy URL to Clipboard`)
            }
          />
        )}
      </Menu>
    </Box>
  );
};

function SinglePostButton({ row }: { row: { id: string; platform: string } }) {
  const url = `/post?id=${row.id}&platform=${row.platform}`;
  return (
    <Tooltip title="Open single post view">
      <MenuItem component="a" href={url}>
        Go to post
      </MenuItem>
    </Tooltip>
  );
}

const SharePostButton = ({ post }: { post: SearchResult }) => {
  const sharePost = trpc.sharing.sharePost.useMutation();

  const media = post.media[0] ?? null;
  // TODO: Can media be nullable? Not sure what internal service to share post expects
  const postToShare = zSharedPost.parse({ ...post, media: media ?? "" });

  const [shared, setShared] = useState<boolean>(false);
  const [tooltipText, setTooltipText] = useState<string>(
    "Click to share. Adjust recipients in account settings.",
  );

  const notifySuccessfulShare = (emails: string[]) => {
    setShared(true);
    const recipients = new Array(emails);
    if (recipients.length === 1) {
      setTooltipText(`This post recently shared with ${recipients[0]}.`);
    } else if (recipients.length === 2) {
      setTooltipText(
        `This post recently shared with ${recipients[0]} and ${recipients[1]}.`,
      );
    } else {
      setTooltipText(
        `This post recently shared with ${recipients[0]}, ${
          recipients[1]
        }, and ${recipients.length - 2} others.`,
      );
    }
    setTimeout(function () {
      setShared(false);
    }, 5000);
  };

  const clickSharePost = () => {
    sharePost.mutate(
      { post: postToShare },
      {
        onSuccess: (data) => {
          notifySuccessfulShare(data);
        },
      },
    );
  };

  return (
    <Tooltip title={tooltipText}>
      <MenuItem onClick={clickSharePost} disabled={shared}>
        Quick share
      </MenuItem>
    </Tooltip>
  );
};

const CopyLinkToClipboardButton = ({
  url,
  trackActionFn,
}: {
  url: string;
  trackActionFn: () => void;
}) => {
  const defaultTooltipText = `Copy source link to clipboard (${
    new URL(url).hostname
  })`;
  const [tooltipText, setTooltipText] = useState(defaultTooltipText);

  return (
    <Tooltip title={tooltipText}>
      <MenuItem
        onClick={() => {
          trackActionFn();
          navigator.clipboard
            .writeText(url)
            .then(() => setTooltipText("Link copied"));
        }}
      >
        Copy url
      </MenuItem>
    </Tooltip>
  );
};

const NavigateToSourceButton = ({
  url,
  trackActionFn,
}: {
  url: string;
  trackActionFn: () => void;
}) => {
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);

  const confirmDialogContentText = (
    <Typography sx={{ mb: 1 }}>
      {`You're trying to open a link that is outside of Pyrra's control. This
      link has not been vetted, so please use caution.`}
    </Typography>
  );

  const dialogCancelButton = (
    <Button onClick={() => setConfirmDialogIsOpen(false)}>Cancel</Button>
  );

  const dialogConfirmButton = (
    <Button
      onClick={() => {
        trackActionFn();
        setConfirmDialogIsOpen(false);
        window.open(url, "_blank");
      }}
    >
      Go
    </Button>
  );

  return (
    <>
      <Tooltip title={`Open source link in new tab (${new URL(url).hostname})`}>
        <MenuItem
          onClick={() => {
            setConfirmDialogIsOpen(true);
          }}
        >
          Go to source
        </MenuItem>
      </Tooltip>
      <ConfirmDialog
        isOpen={confirmDialogIsOpen}
        onClose={() => setConfirmDialogIsOpen(false)}
        title="Warning!"
        contentText={confirmDialogContentText}
        confirmButton={dialogConfirmButton}
        cancelButton={dialogCancelButton}
      />
    </>
  );
};
